import { useEffect, useRef, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { Button } from '@/components/ui/button';
import { useAppSelector } from '@/store/hooks';
import { useVerificationEmail } from '../../../../hooks/useVerificationEmail';
import { buildVerifiedCallbackPath } from '../../../../utils/verifiedRedirect';

/** One automatic send per user per browser in this window (new tabs and
 * relaunches included); Resend stays manual. */
export const CONFIRM_EMAIL_AUTO_SEND_TTL_MS = 10 * 60 * 1000;

export const confirmEmailAutoSendKey = (userId: string) =>
  `ora:confirm-email-access-sent:${userId}`;

const claimAutoSend = (userId: string): boolean => {
  try {
    const key = confirmEmailAutoSendKey(userId);
    const sentAt = Number(localStorage.getItem(key));
    const now = Date.now();
    if (sentAt && now - sentAt < CONFIRM_EMAIL_AUTO_SEND_TTL_MS) return false;
    localStorage.setItem(key, String(now));
    return true;
  } catch {
    // Storage blocked: never auto-send rather than send on every mount.
    return false;
  }
};

export const CONFIRM_EMAIL_NOTE =
  'Opened it somewhere else? Sign in there first, or reset your password — that confirms your email too.';

/** The native shells: the link opens in the system browser, which has no
 * session of the app's. */
export const CONFIRM_EMAIL_NATIVE_NOTE =
  'The link opens in your browser. Sign in to Ora there first (app.oraai.com), then open the link. Or reset your password — that confirms your email too.';

export const STILL_UNCONFIRMED_MESSAGE =
  "We can't see it confirmed yet. Open the link where you're signed in to Ora, then try again.";

interface ConfirmEmailForAccessProps {
  /** What the confirmation unlocks, as the first sentence (`full` only). */
  intro?: string;
  /** Where the link lands once it verifies (a same-origin path). */
  next: string;
  /**
   * Reloads the access state; resolves true while the email still needs
   * confirming. The parent swaps this component out once it is confirmed.
   */
  onRecheck: () => Promise<boolean>;
  /** `banner`: one line and a Send Link button, no automatic send. */
  variant?: 'full' | 'banner';
  context?: 'default' | 'modal';
  /** A Not Now button (`full` only) that calls this. */
  onNotNow?: () => void;
}

/**
 * Institutional access waits for a confirmed email (ORA-994). The link only
 * verifies in a browser where this account is signed in (ORA-1089), so this
 * re-checks whenever the tab comes back into view.
 */
const ConfirmEmailForAccess = ({
  intro,
  next,
  onRecheck,
  variant = 'full',
  context = 'default',
  onNotNow,
}: ConfirmEmailForAccessProps) => {
  const profile = useAppSelector((state) => state.user.profile);
  const email = profile?.email ?? '';
  const userId = profile?.id ?? '';
  const verification = useVerificationEmail(buildVerifiedCallbackPath(next));
  const [isChecking, setIsChecking] = useState(false);
  const [stillUnconfirmed, setStillUnconfirmed] = useState(false);
  const inFlightRecheck = useRef<Promise<boolean | null> | null>(null);
  const onRecheckRef = useRef(onRecheck);
  onRecheckRef.current = onRecheck;

  const autoSend = variant === 'full';

  useEffect(() => {
    if (!autoSend || !email || !userId || !claimAutoSend(userId)) return;
    void verification.send(email);
    // Once per mounted user: `send` changes identity every render.
  }, [autoSend, email, userId]);

  /** One request at a time: a second caller shares the one in flight.
   * Resolves null when the request failed. */
  const recheck = (): Promise<boolean | null> => {
    if (!inFlightRecheck.current) {
      inFlightRecheck.current = onRecheckRef
        .current()
        .catch(() => null)
        .finally(() => {
          inFlightRecheck.current = null;
        });
    }

    return inFlightRecheck.current;
  };

  // The banner sends nothing on its own, so it has nothing to wait for
  // until a link went out.
  const recheckOnFocus = autoSend || verification.isSent;

  useEffect(() => {
    if (!recheckOnFocus) return;

    const onVisible = () => {
      if (document.visibilityState === 'visible') void recheck();
    };

    window.addEventListener('focus', onVisible);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('focus', onVisible);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [recheckOnFocus]);

  const handleConfirmed = async () => {
    setIsChecking(true);
    setStillUnconfirmed(false);
    const pending = await recheck();
    setIsChecking(false);
    setStillUnconfirmed(pending !== false);
  };

  const resendLabel =
    verification.cooldownSeconds > 0
      ? `Resend in ${verification.cooldownSeconds}s`
      : 'Resend';

  const sendDisabled =
    !email || verification.isSending || verification.cooldownSeconds > 0;

  if (variant === 'banner') {
    return (
      <div
        className="mb-4 flex flex-wrap items-center gap-3 rounded-md border border-border-subtle bg-surface-muted px-4 py-3 text-sm text-fg-secondary"
        role="status"
      >
        <p className="!my-0 flex-1">
          {verification.isSent
            ? `We sent a confirmation link to ${email}. Open it in this browser.`
            : "Confirm your email to see all the cohorts you've been added to."}
        </p>
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={sendDisabled}
          onClick={() => void verification.send(email)}
        >
          {verification.isSent ? resendLabel : 'Send Link'}
        </Button>
        {verification.error && (
          <p className="!my-0 w-full text-status-danger">
            {verification.error}
          </p>
        )}
      </div>
    );
  }

  const isNative = Capacitor.isNativePlatform();

  return (
    <div className="flex flex-col gap-3 text-left" role="status">
      <p className="!my-0">
        {intro} We sent a confirmation link to{' '}
        <span className="font-medium text-fg-primary">{email}</span>.
        {!isNative &&
          " Open it on this device, in this browser, where you're signed in."}
      </p>
      <p className="!my-0 text-sm text-fg-secondary">
        {isNative ? CONFIRM_EMAIL_NATIVE_NOTE : CONFIRM_EMAIL_NOTE}
      </p>
      {verification.error && (
        <p className="!my-0 text-sm text-status-danger">{verification.error}</p>
      )}
      {stillUnconfirmed && (
        <p className="!my-0 text-sm text-status-danger">
          {STILL_UNCONFIRMED_MESSAGE}
        </p>
      )}
      <div className="flex flex-col gap-2 mobile:flex-row">
        <Button
          type="button"
          context={context}
          className="flex-1"
          disabled={isChecking}
          onClick={() => void handleConfirmed()}
        >
          {isChecking ? 'Checking...' : "I've Confirmed My Email"}
        </Button>
        <Button
          type="button"
          variant="outline"
          context={context}
          className="flex-1"
          disabled={sendDisabled}
          onClick={() => void verification.send(email)}
        >
          {resendLabel}
        </Button>
      </div>
      {onNotNow && (
        <Button
          type="button"
          variant="ghost"
          context={context}
          className="self-center"
          onClick={onNotNow}
        >
          Not Now
        </Button>
      )}
    </div>
  );
};

export default ConfirmEmailForAccess;
